import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate, SECTOR_META } from "@/lib/format";
import { ResultSummary } from "@/components/ResultSummary";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Copy, Download, Pencil, Loader2, MapPin, Calendar, Building } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { printQuote } from "@/lib/printQuote";
import { QuoteModeDialog } from "@/components/QuoteModeDialog";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => {
      toast.error("Projet introuvable");
      navigate("/projects");
    });
  }, [id]);

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/projects/${id}/duplicate`);
      toast.success("Projet dupliqué");
      navigate(`/projects/${data.id}`);
    } catch {
      toast.error("Échec");
    }
  };

  const exportCsv = () => {
    const rows = [["Fiche", "Opération", "Bloc", "Secteur", "kWh cumac", "Prime (€)"]];
    const rr = project.result || {};
    (rr.operations || []).filter((o) => !o.is_option).forEach((o) => rows.push([o.fiche_code, o.op_name || o.name, o.block_name || "", o.sector, o.kwhc, o.prime]));
    rows.push(["", "", "", rr.options_count ? "TOTAL FERME" : "TOTAL", rr.total_kwhc, rr.total_prime]);
    if (rr.options_count) {
      (rr.operations || []).filter((o) => o.is_option).forEach((o) => rows.push([o.fiche_code, o.op_name || o.name, o.block_name || "", "OPTION", o.kwhc, o.prime]));
      rows.push(["", "", "", "TOTAL AVEC OPTIONS", rr.total_with_options_kwhc, rr.total_with_options_prime]);
    }
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${project.name}.csv`;
    a.click();
  };

  const [quoteOpen, setQuoteOpen] = useState(false);
  const [fichesByCode, setFichesByCode] = useState({});
  useEffect(() => { api.get("/fiches").then((r) => setFichesByCode(Object.fromEntries(r.data.map((f) => [f.code, f])))).catch(() => {}); }, []);
  const exportPdf = (mode) => { setQuoteOpen(false); printQuote(project, user, mode, fichesByCode); api.post(`/projects/${project.id}/quote-generated`, null, { params: { mode } }).catch(() => {}); };

  if (!project) {
    return (
      <div className="grid place-items-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-genis" />
      </div>
    );
  }

  const blocks = project.sector_blocks || [];

  const isOwner = project.user_id === user?.id;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate(isOwner ? "/projects" : "/admin")} className="text-muted-foreground" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-1" /> {isOwner ? "Mes projets" : "Administration"}
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-3xl font-extrabold font-heading tracking-tight text-genis-deep dark:text-white">{project.name}</h1>
            <Badge variant="outline" className="capitalize">{project.status}</Badge>
          </div>
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-muted-foreground">
            {project.client && <span className="inline-flex items-center gap-1"><Building className="h-4 w-4" /> {project.client}</span>}
            <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {project.postal_code || "—"} · Zone {project.zone}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" /> {fmtDate(project.created_at)}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {isOwner ? (
            <>
              <Button variant="outline" onClick={() => navigate(`/simulation/new?id=${project.id}`)} data-testid="edit-project-btn">
                <Pencil className="h-4 w-4 mr-1.5" /> Modifier
              </Button>
              <Button variant="outline" onClick={duplicate} data-testid="duplicate-project-btn">
                <Copy className="h-4 w-4 mr-1.5" /> Dupliquer
              </Button>
            </>
          ) : (
            <Badge variant="secondary" className="self-center" data-testid="readonly-badge">Consultation admin (lecture seule)</Badge>
          )}
          <Button className="bg-genis hover:bg-genis-dark text-white" onClick={() => setQuoteOpen(true)} data-testid="export-pdf-btn">
            <Download className="h-4 w-4 mr-1.5" /> Devis PDF
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-project-btn">
            <Download className="h-4 w-4 mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      <QuoteModeDialog open={quoteOpen} onOpenChange={setQuoteOpen} onPick={exportPdf} />

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5 h-fit">
          <h4 className="font-heading font-bold text-genis-deep dark:text-white mb-3">Paramètres du projet</h4>
          <dl className="space-y-2.5 text-sm">
            {[
              ["Code postal", project.postal_code || "—"],
              ["Zone climatique", project.zone],
              ["Bâtiment > 2 ans", project.building_over_2y ? "Oui" : "Non"],
              ["Date d'engagement", project.engagement_date ? fmtDate(project.engagement_date) : "—"],
              ["Prix du CEE", `${project.cee_price} €/MWhc`],
              ...(project.result?.has_precarite && project.cee_price_precaire ? [["Prix CEE précarité", `${project.cee_price_precaire} €/MWhc`]] : []),
              ["Bonification CPE", project.cpe_enabled ? `Activée (×${project.cpe_coefficient})` : "Non"],
              ["Bonification ZNI", project.zni_enabled ? `Activée (×${project.zni_coefficient})` : "Non"],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-border/60 pb-2">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="font-medium text-right">{v}</dd>
              </div>
            ))}
          </dl>
          {blocks.length > 0 && (
            <div className="mt-4">
              <div className="text-sm text-muted-foreground mb-2">Secteurs</div>
              <div className="flex flex-wrap gap-2">
                {blocks.map((b, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs" data-testid={`detail-block-${i}`}>
                    <span className="h-2 w-2 rounded-full" style={{ background: SECTOR_META[b.sector]?.color || "#33ADF5" }} />
                    <span className="font-semibold">{b.name || SECTOR_META[b.sector]?.label}</span>
                    {b.name && <span className="text-muted-foreground">· {SECTOR_META[b.sector]?.label}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>

        <ResultSummary result={project.result} />
      </div>
    </div>
  );
}
